<template>
  <!-- Pinned side panel (desktop, ≥lg) / full-screen (mobile & tablet, <lg).
       Mounted only while open, so closing discards the local conversation —
       but an unseeded reopen resumes the user's latest server-side chat
       (canResumeLatest default). The host lives once in the layout, so the
       panel survives client-side navigation while open.
       Desktop: flush to the right viewport edge, full height, separated from the
       (squeezed) page by a left border. The page squeeze itself lives in the
       layout — see default.vue.
       z-10 puts the panel on the shared top app layer. The DS modal & backdrop
       (teleported to <body>, after the app root) sit at the same z-10 but paint
       later, so dialogs/drawers/overlays always render on top of the assistant.
       The page header (TheHeader) is also z-10; the host is rendered last in the
       layout so, at equal z-index, the panel paints above the header. Don't raise
       this above z-10, or the panel would cover the DS modals again.
       Mobile/tablet height/transform come from panelStyle (useVisualViewport),
       not the h-[100dvh] class below: 100dvh doesn't shrink for the on-screen
       keyboard, which is what h-[100dvh] alone used to leave cropped. -->
  <Transition
    enter-active-class="lg:transition-transform lg:duration-[250ms] lg:ease-out"
    enter-from-class="lg:translate-x-full"
    leave-active-class="lg:transition-transform lg:duration-[250ms] lg:ease-out"
    leave-to-class="lg:translate-x-full"
  >
    <div
      v-if="store.isOpen"
      :aria-label="i18n(headerTranslations.title)"
      :aria-modal="!isDesktop"
      class="bg-surface-default-low mood-warm fixed z-10 overflow-hidden"
      :class="
        isDesktop
          ? 'border-static-default-hi right-0 top-0 h-[100dvh] border-l'
          : 'inset-0 h-[100dvh]'
      "
      role="dialog"
      :style="panelStyle"
    >
      <ScrollLock>
        <ShoppingAssistantChat
          :key="store.conversationKey"
          :entry-point="store.entryPoint!"
          :initial-chat-id="null"
          :is-seeded-launch
          :page-context="store.pageContext"
          :seeded-message="store.seededMessage"
        />
      </ScrollLock>
    </div>
  </Transition>

  <!-- Shared confirmation for both new-chat and close (gated on a non-empty
       conversation by the web-sidebar host adapter). Rendered above the panel
       by the DS portal so it is never occluded. -->
  <RevDialog
    :name="WEB_SIDEBAR_CONFIRM_DIALOG_NAME"
    :title="currentDialog?.title ?? ''"
    @close="handleDialogClose"
  >
    <template #body>
      <p class="body-1">{{ currentDialog?.description }}</p>
    </template>

    <template #footer>
      <RevButton full-width="always" variant="primary" @click="acceptDialog">
        {{ currentDialog?.acceptButtonText }}
      </RevButton>
      <RevButton
        class="mt-12"
        full-width="always"
        variant="secondary"
        @click="declineDialog"
      >
        {{ currentDialog?.declineButtonText }}
      </RevButton>
    </template>
  </RevDialog>
</template>

<script lang="ts" setup>
import { navigateTo, useRoute } from '#imports'
import { computed, provide, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevDialog } from '@ds/components/Dialog'

import { useVisualViewport } from '../../webviews/useVisualViewport'
import { useSeededSidebarLaunch } from '../composables/useSeededSidebarLaunch'
import { useShoppingAssistantBrowsingContext } from '../composables/useShoppingAssistantBrowsingContext'
import { useShoppingAssistantHashContext } from '../composables/useShoppingAssistantHashContext'
import { SHOPPING_ASSISTANT_HOST_KEY } from '../composables/useShoppingAssistantHost.injection-key'
import { useShoppingAssistantViewport } from '../composables/useShoppingAssistantViewport'
import {
  WEB_SIDEBAR_CONFIRM_DIALOG_NAME,
  useWebSidebarShoppingAssistantHost,
} from '../composables/useWebSidebarShoppingAssistantHost'
import { ASSISTANT_PANEL_WIDTH_PX } from '../constants'
import newConversationTranslations from '../newConversation.translations'
import ShoppingAssistantChat from '../pages/ShoppingAssistantChat.vue'
import headerTranslations from '../pages/components/Header/Header.translations'
import { useShoppingAssistantWebSidebar } from '../stores/useShoppingAssistantWebSidebar'
import { removeSeededMessageFromHash } from '../utils/removeSeededMessageFromHash'

import ScrollLock from './ScrollLock.vue'

const route = useRoute()
const i18n = useI18n()
const tracking = useTracking()
const store = useShoppingAssistantWebSidebar()
const { isDesktop, isOverlay } = useShoppingAssistantViewport()

// The host itself doesn't unmount just because the panel closes (only
// ShoppingAssistantChat, gated on store.isOpen, does that) — it unmounts only
// when the route opts out entirely (e.g. /help — see default.vue). So the
// keyboard-avoidance tracking below is only actually enabled while the
// panel is open — see useVisualViewport's `enabled` param.
const { style: viewportStyle } = useVisualViewport(() => isOverlay.value)

const {
  host,
  currentDialog,
  acceptDialog,
  declineDialog,
  handleDialogClose,
  chatId,
} = useWebSidebarShoppingAssistantHost()
provide(SHOPPING_ASSISTANT_HOST_KEY, host)

useShoppingAssistantBrowsingContext(chatId)

const isSeededLaunch = computed(
  () => Boolean(store.seededMessage) || Boolean(store.pageContext),
)

// Deep-link entry point: seeds the sidebar from `#assistant_message=...` on
// any page (external/shareable or onsite links). Reactive to route.hash
// rather than a one-off onMounted check, since an onsite link changes the
// hash via client-side navigation without necessarily remounting this host —
// and `{ immediate: true }` on the watch below handles the seed correctly on
// a first mount or a remount (e.g. after an opt-out route detour) alike.
const { seededMessage: hashSeededMessage, isSeededLaunch: isHashSeededLaunch } =
  useShoppingAssistantHashContext()

const { maybeLaunch: maybeSeededSidebarLaunch } = useSeededSidebarLaunch({
  isSeededLaunch: isHashSeededLaunch,
  seededMessage: hashSeededMessage,
  isOpen: computed(() => store.isOpen),
  open: store.open,
  restart: store.restart,
  newChat: host.newChat,
  dialog: {
    title: i18n(newConversationTranslations.title),
    description: i18n(newConversationTranslations.description),
    acceptButtonText: i18n(newConversationTranslations.acceptButtonText),
    declineButtonText: i18n(newConversationTranslations.declineButtonText),
  },
  trackSeededOpen: () => {
    tracking.trackClick({
      zone: 'seeded_link',
      name: 'ai_shopping_assistant_opened',
      entryPoint: 'seeded_link',
      seeded: true,
    })
  },
})

// One-shot: consume and strip the seed as soon as it's handled (whether that
// means opening immediately or just surfacing the confirm dialog), so a
// refresh — or the user closing the panel then refreshing — doesn't replay
// or re-prompt the same seed. `{ immediate: true }` also covers the initial
// load, since the hash never reaches the server to be handled during SSR.
// Only the assistant_message key is stripped — the hash is a namespace
// shared with other scopes (product-list filters, TOC anchors, etc.), so any
// other param riding along must survive the clear.
watch(
  () => route.hash,
  () => {
    if (maybeSeededSidebarLaunch()) {
      navigateTo(
        {
          path: route.path,
          query: route.query,
          hash: removeSeededMessageFromHash(route.hash),
        },
        { replace: true },
      )
    }
  },
  { immediate: true },
)

const panelStyle = computed(() =>
  isDesktop.value
    ? { width: `${ASSISTANT_PANEL_WIDTH_PX}px` }
    : viewportStyle.value,
)
</script>
